import { createContext, useContext, useReducer, useEffect } from 'react';

import { roundTotalPrice } from '../helpers/pricing';
import { isStripePaymentRequired } from '../utils/featureFlags';

const initialState = {
  orderDetails: {},
  totalDiscount: null,
  promoCodeApplied: null,
  loadingCustomer: false,
  errorCustomer: null,
  customer: {},
  paymentMethodType: 'new',
  paymentMethod: null,
  upsells: {},
  loadingCreatePaymentIntent: false,
  errorCreatePaymentIntent: null,
  isSubscription: false,
  pollingSubscriptionStatus: false,
  errorSubscriptionStatus: null,
  fetchingProrationPreview: false,
  loadingPreviewSubscriptionDiscount: false,
  errorPreviewSubscriptionDiscount: null,
};

export const SET_ORDER_DETAILS = 'set-order-details';
export const SET_CUSTOMER = 'set-customer';
export const SET_LOADING_CUSTOMER = 'set-loading-customer';
export const SET_ERROR_CUSTOMER = 'set-error-customer';
export const SET_PAYMENT_METHOD = 'set-payment-method';
export const SET_UPSELLS = 'set-upsells';
export const SET_LOADING_CREATE_PAYMENT_INTENT =
  'set-loading-create-payment-intent';
export const SET_ERROR_CREATE_PAYMENT_INTENT =
  'set-error-create-payment-intent';
export const SET_IS_SUBSCRIPTION = 'set-is-subscription';
export const SET_POLLING_SUBSCRIPTION_STATUS =
  'set-polling-subscription-status';
export const SET_ERROR_SUBSCRIPTION_STATUS = 'set-error-subscription-status';
export const SET_FETCHING_PRORATION_PREVIEW = 'set-fetching-proration-preview';
export const SET_LOADING_PREVIEW_SUBSCRIPTION_DISCOUNT =
  'set-loading-preview-subscription-discount';
export const SET_ERROR_PREVIEW_SUBSCRIPTION_DISCOUNT =
  'set-error-preview-subscription-discount';
export const SET_PROMO_CODE_APPLIED = 'set-promo-code-applied';

function reducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case SET_ORDER_DETAILS: {
      return {
        ...state,
        orderDetails: {
          ...state.orderDetails,
          ...payload,
        },
      };
    }
    case SET_CUSTOMER: {
      return {
        ...state,
        customer: payload,
      };
    }
    case SET_LOADING_CUSTOMER: {
      return { ...state, loading: payload };
    }
    case SET_ERROR_CUSTOMER: {
      return { ...state, error: payload };
    }
    case SET_PAYMENT_METHOD: {
      const { paymentMethod, paymentMethodType } = payload;
      return {
        ...state,
        paymentMethod: paymentMethod ? paymentMethod : state.paymentMethod,
        paymentMethodType: paymentMethodType
          ? paymentMethodType
          : state.paymentMethodType,
      };
    }
    case SET_UPSELLS: {
      return {
        ...state,
        upsells: payload,
      };
    }
    case SET_LOADING_CREATE_PAYMENT_INTENT: {
      return {
        ...state,
        loadingCreatePaymentIntent: payload,
      };
    }
    case SET_ERROR_CREATE_PAYMENT_INTENT: {
      return {
        ...state,
        errorCreatePaymentIntent: payload,
      };
    }
    case SET_IS_SUBSCRIPTION: {
      return {
        ...state,
        isSubscription: payload,
      };
    }
    case SET_POLLING_SUBSCRIPTION_STATUS: {
      return {
        ...state,
        pollingSubscriptionStatus: payload,
      };
    }
    case SET_ERROR_SUBSCRIPTION_STATUS: {
      return {
        ...state,
        errorSubscriptionStatus: payload,
      };
    }
    case SET_FETCHING_PRORATION_PREVIEW: {
      return {
        ...state,
        fetchingProrationPreview: payload,
      };
    }
    case SET_LOADING_PREVIEW_SUBSCRIPTION_DISCOUNT: {
      return {
        ...state,
        loadingPreviewSubscriptionDiscount: payload,
      };
    }
    case SET_ERROR_PREVIEW_SUBSCRIPTION_DISCOUNT: {
      return {
        ...state,
        errorPreviewSubscriptionDiscount: payload,
      };
    }
    case SET_PROMO_CODE_APPLIED: {
      const { name, amount, code } = payload;
      return {
        ...state,
        promoCodeApplied: {
          name,
          code,
        },
        totalDiscount: amount,
      };
    }
    default: {
      return { ...state };
    }
  }
}

const PurchasePageContext = createContext();

export function PurchasePageProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!isStripePaymentRequired()) {
      dispatch({
        type: SET_PAYMENT_METHOD,
        payload: {
          paymentMethod: { id: 'bypass' },
          paymentMethodType: 'bypass',
        },
      });
    }
  }, []);

  const orderPrice = state.orderDetails?.price || 0;
  const upsellPrice = state.upsells?.price || 0;
  const totalPrice = +orderPrice + +upsellPrice;

  const value = {
    state: { ...state, totalPrice: roundTotalPrice(+totalPrice) },
    dispatch,
  };

  return (
    <PurchasePageContext.Provider value={value}>
      {children}
    </PurchasePageContext.Provider>
  );
}

export function usePurchasePage() {
  const context = useContext(PurchasePageContext);
  if (!context) {
    throw new Error(
      'usePurchasePage must be used within a PurchasePageProvider'
    );
  }
  return context;
}
